import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FacebookLogo, LinkedinLogo, XLogo, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "";

const ShareBar = ({ slug, title }) => {
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/site/blog/${slug}/share`;
  const enc = encodeURIComponent(shareUrl);
  const btn = "flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-2 border border-white/20 text-[#D4D4D8] hover:text-white hover:border-[#FFE01A]/70 bg-transparent cursor-pointer transition-colors";
  const open = (u) => window.open(u, "_blank", "noopener,width=640,height=560");
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="blog-share-bar">
      <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">Share</span>
      <button className={btn} data-testid="share-facebook" onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${enc}`)}>
        <FacebookLogo size={15} weight="fill" /> Facebook
      </button>
      <button className={btn} data-testid="share-linkedin" onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`)}>
        <LinkedinLogo size={15} weight="fill" /> LinkedIn
      </button>
      <button className={btn} data-testid="share-x" onClick={() => open(`https://twitter.com/intent/tweet?url=${enc}&text=${encodeURIComponent(title)}`)}>
        <XLogo size={15} weight="fill" /> X
      </button>
      <button className={btn} data-testid="share-copy" onClick={async () => {
        try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — paste it anywhere"); }
        catch { toast.error("Couldn't copy — long-press the address bar instead"); }
      }}>
        <LinkSimple size={15} weight="bold" /> Copy link
      </button>
    </div>
  );
};

const renderBlock = (block, i) => {
  const t = block.trim();
  if (!t) return null;
  if (t.startsWith("## ")) {
    const [head, ...rest] = t.split("\n");
    return (
      <React.Fragment key={i}>
        <h2 className="text-2xl font-extrabold mt-8 mb-3 text-white">{head.slice(3)}</h2>
        {rest.length > 0 && <p className="mb-4 leading-relaxed">{rest.join(" ")}</p>}
      </React.Fragment>
    );
  }
  if (t.split("\n").every((l) => l.trim().startsWith("- "))) {
    return (
      <ul key={i} className="list-disc pl-6 mb-4 space-y-1.5">
        {t.split("\n").map((l, j) => <li key={j}>{l.trim().slice(2)}</li>)}
      </ul>
    );
  }
  return <p key={i} className="mb-4 leading-relaxed">{t.split("\n").map((l, j) => <React.Fragment key={j}>{j > 0 && <br />}{l}</React.Fragment>)}</p>;
};

export default function BlogPostPage() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/site/blog/${slug}`).then(({ data }) => {
      setPost(data);
      document.title = `${data.title} — The Self-Agency Project Blog`;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = data.excerpt || data.title;
    }).catch(() => setMissing(true));
    window.scrollTo(0, 0);
  }, [slug]);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#D4D4D8]" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="blog-post-page">
      <header className="max-w-3xl mx-auto px-5 pt-8 flex items-center justify-between">
        <button onClick={() => nav("/blog")} className="bg-transparent border-0 text-[#A1A1AA] hover:text-white cursor-pointer flex items-center gap-2 text-sm" data-testid="blog-post-back">
          <ArrowLeft size={16} /> All articles
        </button>
        <div style={{ fontFamily: "Anton, sans-serif" }} className="text-lg text-white">THE SELF-AGENCY <span className="text-[#FFE01A]">PROJECT</span></div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12">
        {missing && <p data-testid="blog-post-missing">Article not found. <button className="text-[#FFE01A] underline bg-transparent border-0 cursor-pointer" onClick={() => nav("/blog")}>Back to the blog</button></p>}
        {post && (
          <article>
            <div className="text-[#FFE01A] text-xs uppercase tracking-widest mb-2">{fmtDate(post.published_at)}</div>
            <h1 style={{ fontFamily: "Anton, sans-serif" }} className="text-3xl sm:text-4xl uppercase text-white leading-tight mb-4" data-testid="blog-post-title">{post.title}</h1>
            <ShareBar slug={slug} title={post.title} />
            {post.cover_url && <img src={post.cover_url} alt={post.title} className="w-full max-h-[420px] object-cover mb-8" />}
            <div className="text-[15px]" data-testid="blog-post-content">
              {(post.content || "").replace(/\r\n/g, "\n").split("\n\n").map(renderBlock)}
            </div>
            <div className="mt-12 border border-[#FFE01A]/40 bg-[#FFE01A]/5 p-6 text-center">
              <div className="text-white font-extrabold mb-1">Ready to build real-world confidence?</div>
              <p className="text-sm text-[#D4D4D8] mb-4">Online training from $48 — the first 5 lessons are free.</p>
              <button onClick={() => nav("/welcome")} data-testid="blog-post-cta"
                className="bg-[#FFE01A] text-black font-bold uppercase text-sm tracking-wide px-6 py-3 border-0 cursor-pointer">
                Start Free Today
              </button>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
